import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AirplaneTilt, Bed, Car, CaretRight } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { directionOf, pick } from "@/lib/languages";
import { formatDate, formatDateRange } from "@/lib/format";
import { clearRecentSearches, restoreRecentSearches, useRecentSearches } from "@/lib/recentSearches";
import { homeL } from "@/i18n/home";
import HomeSection from "./HomeSection";

const ICONS = { stays: Bed, flights: AirplaneTilt, cars: Car };
const SHOWN = 4;

/**
 * "Pick up where you left off": the visitor's own last searches on this
 * device (lib/recentSearches), each card reopening that exact results page.
 * Nothing when there are none; never someone else's searches, never a
 * search whose dates have passed.
 *
 * "Clear all" acts at once and offers to undo it in the band's place, focus
 * on "Undo": a slip of the finger costs nothing. Undo puts the searches back.
 */
export default function RecentSearches({ onCleared }) {
  const items = useRecentSearches();
  const { lang, siteLang = lang, t } = useSettings();
  const l = pick(homeL, siteLang).recent;
  const [removed, setRemoved] = useState(null);
  const undoRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { if (removed) undoRef.current?.focus(); }, [removed]);

  if (!items.length && removed) {
    const undo = () => {
      restoreRecentSearches(removed);
      setRemoved(null);
      requestAnimationFrame(() => listRef.current?.focus());
    };
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-x-4 gap-y-1 px-4 sm:px-6 lg:px-8" data-testid="recent-undo">
        <p role="status" className="text-body-sm text-touki-black">{l.cleared}</p>
        <button ref={undoRef} type="button" onClick={undo} data-testid="recent-undo-btn"
          className="inline-flex min-h-11 items-center rounded-sm px-1 text-label-lg font-semibold text-touki-black underline decoration-touki-field underline-offset-4 transition-colors duration-fast hover:decoration-touki-black">
          {l.undo}
        </button>
      </div>
    );
  }
  if (!items.length) return null;
  const clear = () => {
    setRemoved(clearRecentSearches());
    onCleared?.(l.cleared);
  };
  return (
    <HomeSection id="recent" title={l.title} note={l.note} data-testid="home-recent"
      action={(
        <button type="button" onClick={clear} data-testid="recent-clear"
          className="inline-flex min-h-11 items-center rounded-sm px-1 text-label-lg font-semibold text-touki-black underline decoration-touki-field underline-offset-4 transition-colors duration-fast hover:decoration-touki-black">
          {l.clear}<span className="sr-only">: {l.clearLabel}</span>
        </button>
      )}>
      <ul ref={listRef} tabIndex={-1} aria-label={l.title} className="grid gap-3 focus:outline-none sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        {items.slice(0, SHOWN).map((item) => (
          <li key={`${item.href}|${item.start}`} className="min-w-0">
            <RecentCard item={item} lang={lang} l={l} to={t("search.to")} />
          </li>
        ))}
      </ul>
    </HomeSection>
  );
}

function RecentCard({ item, lang, l, to }) {
  const Icon = ICONS[item.kind];
  const arrow = directionOf(lang) === "rtl" ? "←" : "→";
  const when = item.end
    ? formatDateRange(item.start, item.end, { lang, weekday: false })
    : formatDate(item.start, { lang, weekday: false });
  const people = item.people ? (item.kind === "stays" ? l.guests(item.people) : l.travellers(item.people)) : "";
  return (
    <Link to={item.href} data-testid={`recent-${item.kind}`}
      className="flex h-full min-h-[76px] items-center gap-3 rounded-lg border border-touki bg-white p-4 transition-colors duration-fast hover:border-touki-field sm:p-5">
      <Icon size={20} weight="bold" aria-hidden="true" className="shrink-0 text-touki-muted" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="sr-only">{l.kinds[item.kind]}: </span>
        <span className="line-clamp-2 text-heading-sm font-bold text-touki-black [overflow-wrap:anywhere]">
          {item.kind === "flights" ? (
            <>{item.from} <span aria-hidden="true">{arrow}</span><span className="sr-only"> {to} </span> {item.to}</>
          ) : item.place}
        </span>
        <span className="text-body-sm text-touki-muted tabular-nums">{people ? `${when}, ${people}` : when}</span>
      </span>
      <CaretRight size={18} weight="bold" aria-hidden="true" className="shrink-0 text-touki-black rtl:-scale-x-100 lg:hidden" />
    </Link>
  );
}
